/**
 * SEO Helper Functions
 * Centralized SEO management for dynamic meta tags, structured data, and Open Graph
 */

/**
 * Update page meta tags dynamically for better SEO
 * @param {Object} options - SEO options
 */
export const updateMetaTags = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = "website",
  canonical,
  structuredData,
}) => {
  // Update document title
  if (title) {
    document.title = title;
  }

  // Helper function to update or create meta tag
  const setMeta = (selector, attribute, content) => {
    let element = document.querySelector(selector);
    if (!element) {
      element = document.createElement("meta");
      if (selector.includes("property")) {
        element.setAttribute(
          "property",
          selector.replace(/meta\[property="([^"]+)"\]/, "$1")
        );
      } else {
        element.setAttribute(
          "name",
          selector.replace(/meta\[name="([^"]+)"\]/, "$1")
        );
      }
      document.head.appendChild(element);
    }
    element.setAttribute("content", content);
  };

  // Update standard meta tags
  if (description) {
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[name="twitter:description"]', "content", description);
  }

  if (keywords) {
    setMeta('meta[name="keywords"]', "content", keywords);
  }

  // Update Open Graph tags
  if (title) {
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[name="twitter:title"]', "content", title);
  }

  if (ogImage) {
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[name="twitter:image"]', "content", ogImage);
  }

  if (ogType) {
    setMeta('meta[property="og:type"]', "content", ogType);
  }

  // Update canonical URL
  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // Also update og:url
    setMeta('meta[property="og:url"]', "content", canonical);
  }

  // Add structured data
  if (structuredData) {
    addStructuredData(structuredData);
  }
};

/**
 * Add structured data (JSON-LD) to the page
 * @param {Object} data - Schema.org structured data
 */
export const addStructuredData = (data) => {
  // Remove existing structured data for this page
  const existingScripts = document.querySelectorAll(
    'script[type="application/ld+json"][data-dynamic="true"]'
  );
  existingScripts.forEach((script) => script.remove());

  // Add new structured data
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-dynamic", "true");
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * Generate Article structured data for blog posts
 */
export const generateArticleSchema = ({
  title,
  description,
  author = "Astrologija v Vortexu",
  datePublished,
  dateModified,
  image,
  url,
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: description,
    image: image || "https://www.astrologijavortex.si/images/og-image.jpg",
    author: {
      "@type": "Person",
      name: author,
    },
    publisher: {
      "@type": "Organization",
      name: "Astrologija v Vortexu",
      logo: {
        "@type": "ImageObject",
        url: "https://www.astrologijavortex.si/logo512.png",
      },
    },
    datePublished: datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };
};

/**
 * Generate Service structured data
 */
export const generateServiceSchema = (services) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: services.map((service, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Service",
        name: service.title,
        description: service.description,
        provider: {
          "@type": "Organization",
          name: "Astrologija v Vortexu",
        },
        offers: {
          "@type": "Offer",
          price: service.price,
          priceCurrency: "EUR",
        },
      },
    })),
  };
};

/**
 * Generate FAQ structured data
 */
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

/**
 * Generate breadcrumb structured data
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
};

export default {
  updateMetaTags,
  addStructuredData,
  generateArticleSchema,
  generateServiceSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
};
